'use es6';

import { createTreatmentsResolver } from 'laboratory-lib/core/resolvers';
import { createExposureLogger } from 'laboratory-lib/core/loggers';
import { validateHttp, validateSearch, validateStore, validateTimeout, validateTreatmentsSchema, validateQuickFetch, validateQuickFetchLabel } from 'laboratory-lib/core/validators';
const mockHttpClient = {
  get: () => new Promise((onSuccess, onError) => onError(new Error('no http client implemented'))),
  post: () => new Promise((onSuccess, onError) => onError(new Error('no http client implemented')))
};

function isPromise(thing) {
  return thing && typeof thing.then === 'function' && typeof thing.catch === 'function';
}

function promisify(thing) {
  if (isPromise(thing)) {
    return thing;
  }

  return new Promise(onComplete => onComplete(thing));
}

function adaptAsyncStore(store) {
  let asyncStore;
  return (method, ...args) => {
    if (!store) return promisify();

    if (!asyncStore && typeof store.open === 'function') {
      try {
        asyncStore = store.open();
      } catch (err) {
        asyncStore = promisify();
      }
    }

    return new Promise(onSuccess => {
      const storeTimeout = setTimeout(() => {
        onSuccess(promisify());
      }, 1000);
      asyncStore.then(openStore => {
        clearTimeout(storeTimeout);

        if (!openStore) {
          onSuccess(promisify());
        } else {
          onSuccess(promisify(openStore[method](...args)));
        }
      }).catch(() => {
        clearTimeout(storeTimeout);
        onSuccess(promisify());
      });
    });
  };
}

function validateClientInputs({
  http,
  quickFetch,
  quickFetchLabel,
  search,
  store,
  timeout,
  treatments
}) {
  validateHttp(http, 'createClient');
  validateSearch(search, 'createClient');
  validateStore(store, 'createClient');
  validateTimeout(timeout, 'createClient');
  validateTreatmentsSchema(treatments, 'createClient');

  if (quickFetchLabel) {
    validateQuickFetch(quickFetch, 'createClient');
    validateQuickFetchLabel(quickFetchLabel, 'createClient');
  }
}

export function createClient({
  apiDomain = '',
  ceid = '',
  http = mockHttpClient,
  onError = () => {},
  search = window.location.search,
  store = null,
  quickFetch = null,
  quickFetchLabel = '',
  timeout = 0,
  treatments = {}
} = {}) {
  // throws runtime exceptions
  validateClientInputs({
    http,
    quickFetch,
    quickFetchLabel,
    search,
    store,
    timeout,
    treatments
  });
  const schema = treatments;
  const asyncStore = store ? adaptAsyncStore(store) : undefined;
  const {
    resolveTreatments
  } = createTreatmentsResolver({
    asyncStore,
    apiDomain,
    ceid,
    http,
    onError,
    quickFetch,
    quickFetchLabel,
    schema,
    search,
    timeout
  });
  const {
    logExposure,
    logExposures
  } = createExposureLogger({
    apiDomain,
    http,
    resolveTreatments,
    onError
  });
  return {
    logExposure,
    logExposures,
    resolve: resolveTreatments
  };
}